import React, { useState, useMemo, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, LineChart, ArrowLeftRight, Wallet as WalletIcon,
  Receipt, Landmark, ShieldCheck, LogOut, ChevronDown, Coins, Menu, X, Crown, Rocket, Orbit,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { NAV } from "@/constants/testIds";
import { FIATS } from "@/lib/api";
import OrbitalLogo from "@/components/OrbitalLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const TIER_BADGE = {
  free: { color: "text-[#6E7587] border-[#2A2E38]" },
  intermediate: { color: "text-[#7C5CFF] border-[#7C5CFF]/40" },
  vip: { color: "text-[#D19B54] border-[#D19B54]/50" },
};

const navItemsBase = [
  { to: "/app/dashboard", key: "appnav.dashboard", icon: LayoutDashboard, testid: NAV.sidebarDashboard },
  { to: "/app/markets", key: "appnav.markets", icon: LineChart, testid: NAV.sidebarMarkets },
  { to: "/app/trade", key: "appnav.trade", icon: ArrowLeftRight, testid: NAV.sidebarTrade },
  { to: "/app/staking", key: "appnav.staking", icon: Coins, testid: "sidebar-staking" },
  { to: "/app/wallet", key: "appnav.wallet", icon: WalletIcon, testid: NAV.sidebarWallet },
  { to: "/app/transactions", key: "appnav.transactions", icon: Receipt, testid: NAV.sidebarTransactions },
  { to: "/app/banks", key: "appnav.banks", icon: Landmark, testid: NAV.sidebarBanks },
  { to: "/app/subscription", key: "appnav.subscription", icon: Crown, testid: "sidebar-subscription" },
];

function NavBody({ user, onItemClick, t }) {
  return (
    <nav className="flex-1 px-3 py-4 space-y-1">
      {navItemsBase.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={item.testid}
          onClick={onItemClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
              isActive
                ? "bg-[#1C202B] text-[#F4F1EB] border-l-2 border-[#7C5CFF] pl-[10px]"
                : "text-[#A3A9B8] hover:text-[#F4F1EB] hover:bg-[#141720]"
            }`
          }
        >
          <item.icon size={16} strokeWidth={1.6} />
          <span>{t(item.key)}</span>
        </NavLink>
      ))}
      {user?.role === "admin" && (
        <NavLink
          to="/app/admin"
          data-testid={NAV.sidebarAdmin}
          onClick={onItemClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
              isActive
                ? "bg-[#1C202B] text-[#7C5CFF] border-l-2 border-[#7C5CFF] pl-[10px]"
                : "text-[#D19B54] hover:text-[#F4F1EB] hover:bg-[#141720]"
            }`
          }
        >
          <ShieldCheck size={16} strokeWidth={1.6} />
          <span>{t("appnav.admin")} Console</span>
        </NavLink>
      )}
    </nav>
  );
}

function Brand() {
  return (
    <NavLink to="/" className="flex items-center gap-2">
      <OrbitalLogo size={32} />
      <div>
        <div className="font-display text-lg font-bold leading-none">Orbital</div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587] mt-1">Exchange</div>
      </div>
    </NavLink>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [fiat, setFiat] = useState(
    () => localStorage.getItem("orbital_fiat") || user?.default_fiat || "USD"
  );
  const [fxOpen, setFxOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const changeFiat = (f) => {
    setFiat(f);
    localStorage.setItem("orbital_fiat", f);
    setFxOpen(false);
    window.dispatchEvent(new CustomEvent("orbital:fiat-changed", { detail: f }));
  };

  const handleLogout = () => {
    logout();
    nav("/");
  };

  // close drawer when route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const outletContext = useMemo(() => ({ fiat }), [fiat]);

  return (
    <div className="min-h-screen flex bg-[#0b0d12] text-[#f4f1eb]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 flex-col border-r border-[#2A2E38] bg-[#0B0D12]">
        <div className="px-6 py-6 border-b border-[#2A2E38]">
          <Brand />
        </div>
        <NavBody user={user} t={t} />
        <div className="px-3 py-3 border-t border-[#2A2E38] text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
          Live · CoinGecko
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            data-testid="mobile-drawer"
            className="relative w-72 max-w-[80vw] flex flex-col border-r border-[#2A2E38] bg-[#0B0D12]"
          >
            <div className="px-5 py-5 border-b border-[#2A2E38] flex items-center justify-between">
              <Brand />
              <button
                data-testid="mobile-drawer-close"
                onClick={() => setMobileOpen(false)}
                className="p-2 text-[#A3A9B8] hover:text-[#7C5CFF]"
              >
                <X size={18} />
              </button>
            </div>
            <NavBody user={user} onItemClick={() => setMobileOpen(false)} t={t} />
            <div className="px-3 py-3 border-t border-[#2A2E38] flex items-center justify-between">
              <div>
                <div className="text-xs text-[#F4F1EB]">{user?.full_name}</div>
                <div className="text-[10px] tracking-wider uppercase text-[#6E7587]">
                  {user?.role}
                </div>
              </div>
              <button
                data-testid="mobile-drawer-logout"
                onClick={handleLogout}
                className="p-2 border border-[#2A2E38] rounded-sm text-[#A3A9B8] hover:text-[#7C5CFF] hover:border-[#7C5CFF]"
              >
                <LogOut size={14} />
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-14 border-b border-[#2A2E38] bg-[#0B0D12]/80 backdrop-blur-xl flex items-center justify-between px-3 md:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 -ml-2 text-[#F4F1EB] hover:text-[#7C5CFF]"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            {/* Brand on mobile */}
            <div className="md:hidden">
              <Brand />
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#6E7587]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7C9C69] live-dot"></span>
              Market Live
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language switcher */}
            <LanguageSwitcher />
            {/* Fiat switcher */}
            <div className="relative">
              <button
                data-testid={NAV.topbarFiatSwitcher}
                onClick={() => setFxOpen((s) => !s)}
                className="flex items-center gap-2 px-2.5 py-1.5 border border-[#2A2E38] rounded-sm font-mono text-xs hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors"
              >
                {fiat} <ChevronDown size={12} />
              </button>
              {fxOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-[#141720] border border-[#2A2E38] rounded-sm shadow-2xl z-40">
                  {FIATS.map((f) => (
                    <button
                      key={f}
                      onClick={() => changeFiat(f)}
                      className={`block w-full text-left px-3 py-2 text-xs font-mono hover:bg-[#1C202B] ${
                        f === fiat ? "text-[#7C5CFF]" : "text-[#F4F1EB]"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="hidden sm:flex flex-col text-right leading-tight">
              <span className="text-xs text-[#F4F1EB]">{user?.full_name}</span>
              <span className="text-[10px] tracking-wider uppercase text-[#6E7587]">
                {user?.role}
              </span>
            </div>
            {user?.account_tier && (
              <span
                data-testid="user-tier-badge"
                className={`hidden sm:inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-2 py-1 border rounded-sm font-mono ${TIER_BADGE[user.account_tier]?.color || TIER_BADGE.free.color}`}
                title={`Account tier · ${user.account_tier}`}
              >
                {user.account_tier === "vip" && <Crown size={10} />}
                {user.account_tier === "intermediate" && <Rocket size={10} />}
                {user.account_tier === "free" && <Orbit size={10} />}
                {user.account_tier === "vip" ? "VIP" :
                 user.account_tier === "intermediate" ? "Nova" : "Orbit"}
              </span>
            )}
            <button
              data-testid={NAV.topbarLogout}
              onClick={handleLogout}
              className="hidden sm:block p-2 border border-[#2A2E38] rounded-sm text-[#A3A9B8] hover:text-[#7C5CFF] hover:border-[#7C5CFF] transition-colors"
              title={t("appnav.signout")}
            >
              <LogOut size={14} />
            </button>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-4 md:p-8 overflow-x-hidden">
          <Outlet context={React.useMemo(() => ({ fiat }), [fiat])} />
        </main>
      </div>
    </div>
  );
}
